import React, { useEffect, useMemo, useState } from 'react';
import { FiX, FiChevronDown, FiGrid } from 'react-icons/fi';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { productAPI, categoryAPI } from '../api/services';
import ProductCard from '../components/ProductCard';
import './Products.css';

function buildTree(cats, parentId = null) {
  return cats
    .filter(c => (c.parent_id ?? null) === parentId)
    .map(c => ({ ...c, children: buildTree(cats, c.id) }));
}

function CategoryTree({ nodes, selectedCategory, expandedCats, counts, onSelect, onToggle, depth = 0 }) {
  return (
    <>
      {nodes.map(node => (
        <div key={node.id}>
          <button
            className={`category-btn cat-tree-btn ${selectedCategory === node.id ? 'active' : ''} depth-${Math.min(depth, 2)}`}
            style={{ paddingLeft: 4 + depth * 14 }}
            onClick={() => {
              onSelect(node.id);
              if (node.children.length > 0) onToggle(node.id);
            }}
          >
            {node.children.length > 0 && (
              <span className={`cat-arrow ${expandedCats[node.id] ? 'open' : ''}`}>›</span>
            )}
            <span className="cat-tree-name">{node.name}</span>
            <span className="cat-tree-count">({counts[node.id] ?? '…'})</span>
          </button>

          {node.children.length > 0 && expandedCats[node.id] && (
            <CategoryTree
              nodes={node.children}
              selectedCategory={selectedCategory}
              expandedCats={expandedCats}
              counts={counts}
              onSelect={onSelect}
              onToggle={onToggle}
              depth={depth + 1}
            />
          )}
        </div>
      ))}
    </>
  );
}

const PAGE_SIZE = 40;
const GRID_OPTIONS = [2, 3, 4, 5];
const SORT_OPTIONS = [
  { value: '',           label: 'Best selling' },
  { value: 'price-asc',  label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'name-asc',   label: 'Name: A-Z' },
];

function Products() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [products,    setProducts]    = useState([]);
  const [categories,  setCategories]  = useState([]);
  const [loading,      setLoading]     = useState(true);
  const [totalCount,   setTotalCount]  = useState(0);
  const [expandedCats, setExpandedCats] = useState({});
  const [categoryCounts, setCategoryCounts] = useState({});

  const [collectionOpen,   setCollectionOpen]   = useState(true);
  const [availabilityOpen, setAvailabilityOpen] = useState(true);
  const [gridCols, setGridCols] = useState(3);
  const [sortBy,   setSortBy]   = useState('');
  const [stockFilter, setStockFilter] = useState({ in: true, out: true });

  const rawCat           = searchParams.get('category_id') || searchParams.get('cat');
  const selectedCategory = rawCat ? parseInt(rawCat) : null;
  const searchQuery      = searchParams.get('q') || '';
  const page             = parseInt(searchParams.get('page') || '1');

  useEffect(() => {
    categoryAPI.getCategories().then(r => setCategories(r.data)).catch(() => {});
  }, []);

  // Product count per category, shown next to each name in the sidebar
  useEffect(() => {
    if (categories.length === 0) return;
    Promise.all(categories.map(c =>
      productAPI.countProducts(c.id).then(r => [c.id, r.data?.total || 0]).catch(() => [c.id, 0])
    )).then(pairs => setCategoryCounts(Object.fromEntries(pairs)));
  }, [categories]);

  useEffect(() => {
    if (!selectedCategory || categories.length === 0) return;
    const cat = categories.find(c => c.id === selectedCategory);
    if (!cat) return;
    if (cat.parent_id) {
      setExpandedCats(prev => ({ ...prev, [cat.parent_id]: true }));
    } else {
      setExpandedCats(prev => ({ ...prev, [cat.id]: true }));
    }
  }, [selectedCategory, categories]);

  useEffect(() => {
    setLoading(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const skip = (page - 1) * PAGE_SIZE;
    if (searchQuery) {
      productAPI.searchProducts(searchQuery)
        .then(r => { setProducts(r.data || []); setTotalCount(r.data?.length || 0); })
        .catch(() => { setProducts([]); setTotalCount(0); })
        .finally(() => setLoading(false));
    } else {
      Promise.all([
        productAPI.getProducts(selectedCategory || null, skip, PAGE_SIZE),
        productAPI.countProducts(selectedCategory || null),
      ])
        .then(([prodRes, countRes]) => {
          setProducts(prodRes.data || []);
          setTotalCount(countRes.data?.total || 0);
        })
        .catch(() => { setProducts([]); setTotalCount(0); })
        .finally(() => setLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const goToPage = (p) => {
    const params = new URLSearchParams(searchParams);
    if (p === 1) params.delete('page');
    else params.set('page', p);
    navigate(`/products?${params.toString()}`);
  };

  const handleToggle = (id) => setExpandedCats(prev => ({ ...prev, [id]: !prev[id] }));

  const tree = buildTree(categories);

  const inStockCount  = products.filter(p => p.stock > 0).length;
  const outStockCount = products.length - inStockCount;

  // Sort/filter apply to the currently loaded page only — this store paginates
  // server-side, so "Best selling" etc. would need a backend sort to cover the
  // whole catalogue. Fine for now since pages are 40 products.
  const visibleProducts = useMemo(() => {
    let list = [...products];
    if (sortBy === 'price-asc')  list.sort((a, b) => (a.discount_price ?? a.price) - (b.discount_price ?? b.price));
    if (sortBy === 'price-desc') list.sort((a, b) => (b.discount_price ?? b.price) - (a.discount_price ?? a.price));
    if (sortBy === 'name-asc')   list.sort((a, b) => a.name.localeCompare(b.name));
    return list.filter(p => (p.stock > 0 ? stockFilter.in : stockFilter.out));
  }, [products, sortBy, stockFilter]);

  return (
    <div className="products-page">
      <div className="container">
        <div className="products-page-header">
          <h1>{selectedCategory ? (categories.find(c => c.id === selectedCategory)?.name || 'All Products') : 'All Products'}</h1>
          <p>{selectedCategory ? `Browse all ${categories.find(c => c.id === selectedCategory)?.name || ''} products` : 'Explore our full catalogue'}</p>
        </div>

        <div className="products-layout">
          <aside className="sidebar">
            <div className="sidebar-filter-label">Filter:</div>

            {selectedCategory !== null && (
              <button className="clear-filter-btn" onClick={() => navigate('/products')}>
                <FiX size={16} /> Clear Filter
              </button>
            )}

            <div className="filter-section">
              <button className="filter-section-head" onClick={() => setCollectionOpen(o => !o)}>
                <h3>Collection</h3>
                <FiChevronDown size={16} className={`filter-chevron ${collectionOpen ? 'open' : ''}`} />
              </button>
              {collectionOpen && (
                <div className="category-list">
                  <CategoryTree
                    nodes={tree}
                    selectedCategory={selectedCategory}
                    expandedCats={expandedCats}
                    counts={categoryCounts}
                    onSelect={(id) => navigate(`/products?category_id=${id}`)}
                    onToggle={handleToggle}
                    depth={0}
                  />
                </div>
              )}
            </div>

            <div className="filter-section">
              <button className="filter-section-head" onClick={() => setAvailabilityOpen(o => !o)}>
                <h3>Availability</h3>
                <FiChevronDown size={16} className={`filter-chevron ${availabilityOpen ? 'open' : ''}`} />
              </button>
              {availabilityOpen && (
                <div className="availability-list">
                  <label className="availability-item">
                    <input
                      type="checkbox"
                      checked={stockFilter.in}
                      onChange={e => setStockFilter(f => ({ ...f, in: e.target.checked }))}
                    />
                    In stock ({inStockCount})
                  </label>
                  <label className="availability-item">
                    <input
                      type="checkbox"
                      checked={stockFilter.out}
                      onChange={e => setStockFilter(f => ({ ...f, out: e.target.checked }))}
                    />
                    Out of stock ({outStockCount})
                  </label>
                </div>
              )}
            </div>
          </aside>

          <main className="products-main">
            <div className="products-toolbar">
              <div className="grid-density">
                {GRID_OPTIONS.map(n => (
                  <button
                    key={n}
                    className={`grid-density-btn ${gridCols === n ? 'active' : ''}`}
                    onClick={() => setGridCols(n)}
                    aria-label={`${n} columns`}
                  >
                    <FiGrid size={14} />
                  </button>
                ))}
              </div>

              <div className="products-toolbar-right">
                <label className="sort-by">
                  Sort By:
                  <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                    {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
                <span className="products-count">
                  {loading ? 'Loading…' : searchQuery ? `${visibleProducts.length} results` : `${totalCount} Products`}
                </span>
              </div>
            </div>

            {loading ? (
              <div className="loading"><div className="spinner" /></div>
            ) : visibleProducts.length === 0 ? (
              <div className="no-products"><p>No products found</p></div>
            ) : (
              <>
                <div className="grid products-grid" style={{ gridTemplateColumns: `repeat(${gridCols}, 1fr)` }}>
                  {visibleProducts.map((p, i) => (
                    <div key={p.id} className="product-grid-item" style={{ '--i': i }}>
                      <ProductCard product={p} simple />
                    </div>
                  ))}
                </div>

                {(() => {
                  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
                  if (totalPages <= 1) return null;
                  const btnStyle = (active, disabled) => ({
                    minWidth: 40, height: 40, borderRadius: 8, border: `1.5px solid ${active ? '#111' : '#e2e8f0'}`,
                    background: active ? '#111' : disabled ? '#f8fafc' : '#fff',
                    cursor: disabled ? 'default' : 'pointer',
                    fontWeight: 700, fontSize: '.875rem',
                    color: active ? '#fff' : disabled ? '#94a3b8' : '#1e293b',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 12px',
                  });
                  // Show at most 7 page buttons around current page
                  const pages = [];
                  for (let i = 1; i <= totalPages; i++) {
                    if (i === 1 || i === totalPages || (i >= page - 2 && i <= page + 2)) {
                      pages.push(i);
                    } else if (pages[pages.length - 1] !== '...') {
                      pages.push('...');
                    }
                  }
                  return (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, padding: '32px 0 16px', flexWrap: 'wrap' }}>
                      <button onClick={() => goToPage(page - 1)} disabled={page === 1} style={btnStyle(false, page === 1)}>← Prev</button>
                      {pages.map((p, i) =>
                        p === '...'
                          ? <span key={`d${i}`} style={{ padding: '0 4px', color: '#94a3b8', fontWeight: 700 }}>…</span>
                          : <button key={p} onClick={() => goToPage(p)} style={btnStyle(p === page, false)}>{p}</button>
                      )}
                      <button onClick={() => goToPage(page + 1)} disabled={page === totalPages} style={btnStyle(false, page === totalPages)}>Next →</button>
                    </div>
                  );
                })()}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

export default Products;
